<template>
  <SoDialog v-model="isOpen" card-title="Help" max-width="1000">
    <div class="content" data-testid="sync-accounts-info-modal-content">
      <p>
        This dashboard allows you to synchronise accounts between Junifer and So
        Energy systems. Synchronised accounts are used for testing purposes and
        are required for the automated tests to run.
      </p>
      <figure>
        <img
          src="@/assets/sync-accounts-diagram.png"
          alt="Sync accounts diagram"
        />
        <figcaption>
          Acccount synchronisation diagram in non-production environment
        </figcaption>
      </figure>

      <h5 class="text-h5">Sync status legend</h5>
      <ul>
        <li>
          <SoStatus :status="{ type: 'normal', text: 'On' }" /> - Account is
          synchronised with replicated Junifer tables every 30 minutes.
        </li>
        <li>
          <SoStatus :status="{ type: 'normal', text: 'On (locked)' }" /> -
          Account is synchronised every 30 minutes and cannot be removed from
          the synchrinised accounts list. These accounts are used by QA team for
          test automation.
        </li>
        <li>
          <SoStatus :status="{ type: 'critical', text: 'Off' }" /> - Account is
          not going to be automatically synchronised. You can manually
          synchronise account by clicking <b>Resync Account</b> from the actions
          menu.
        </li>
      </ul>

      <h5 class="text-h5">Identifier properties</h5>

      <ul>
        <li>
          <b>Account Number</b> (
          <pre>accountNumber</pre>
          ,
          <pre>juniferAccountNumber</pre>
          ) - 8 digit account identifier, it is the same between Junifer and So
          Energy systems.
        </li>
        <li>
          <b>Customer Number</b> (
          <pre>customerNumber</pre>
          ,
          <pre>juniferAccountNumber</pre>
          ) - 8 digit customer identifier, it is the same between Junifer and So
          Energy systems.
        </li>
        <li>
          <b>Junifer ID</b> (
          <pre>accountId</pre>
          ,
          <pre>juniferAccountId</pre>
          ) - primary key in the Junifer database in the
          <pre>Account</pre>
          table. Exists only in Junifer Systems.
        </li>
        <li>
          <b>Core ID</b> (
          <pre>coreId</pre>
          ,
          <pre>billingAccountId</pre>
          ) - primary key in the
          <pre>be_customers->billing_accounts</pre>
          table. Exists only in So Energy Systems. If the value shows
          <pre>Not synced</pre>
          that means the account does not exist in the So Energy database yet
          and need to be manually synced by clicking <b>Enable Auto-Sync</b>.
        </li>
        <li>
          <b>User ID</b> (
          <pre>userId</pre>
          ) - primary key in the
          <pre>be_identity->users</pre>
          table. Use only in So Energy system. User account is required to sign
          in to the MyAccount.
        </li>
      </ul>

      <h5 class="text-h5">
        Limitations of synchronisation in non-prod environments
      </h5>

      A maximum of <b>{{ AUTO_SYNCED_ACCOUNT_LIMIT }}</b> accounts can be
      synchronized in the testing environment. Once the limit is reached, adding
      a new account will automatically remove the oldest one from the list to
      make space.
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import { AUTO_SYNCED_ACCOUNT_LIMIT } from "@/constants/syncedAccounts"

  export default defineComponent({
    components: {
      SoDialog,
      SoStatus,
    },
    props: {
      modelValue: {
        type: Boolean,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        AUTO_SYNCED_ACCOUNT_LIMIT,
      }
    },
    computed: {
      isOpen: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
    },
  })
</script>
<style scoped>
  .content b {
    font-weight: 500 !important;
  }

  .content h5 {
    margin-bottom: 0.5rem;
  }
  .content p {
    margin-bottom: 1.5rem;
  }

  .content ul {
    margin-bottom: 1.5rem;
    padding-left: 1.5rem;
  }

  .content ul li {
    margin-bottom: 0.5rem;
  }

  .content pre {
    display: inline;
    background-color: #f5f5f5;
    padding: 0.2rem 0.4rem;
    border-radius: 0.25rem;
    font-size: 0.9rem;
  }

  .content figure {
    width: 100%;
    margin-bottom: 1.5rem;
  }

  .content img {
    width: 100%;
  }

  .content figcaption {
    font-style: italic;
    text-align: center;
  }
</style>
